import type {
  RiskReductionMeasure,
  RiskReductionMeasureInput,
  SafetyFunction,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import type { RiskReductionMeasureFormData } from './RiskReductionMeasureForm.schema'

export function isSafetyFunctionMeasure(
  riskReductionMeasure: RiskReductionMeasure
): boolean {
  if (
    riskReductionMeasure.type === 'SCP' &&
    riskReductionMeasure.dependsOnControlSystem === true
  ) {
    return true
  }

  return false
}

export function isEligibleForSafetyFunctionAssignment(
  data: Pick<RiskReductionMeasureFormData, 'type' | 'dependsOnControlSystem'>
): boolean {
  return data.type === 'SCP' && data.dependsOnControlSystem
}

export type LinkedSafetyFunctionDisplay = {
  name: string
  eplanReferenceId: string | null
}

export function getLinkedSafetyFunctionDisplay(
  safetyFunctionId: string | null | undefined,
  safetyFunctions: SafetyFunction[]
): LinkedSafetyFunctionDisplay | null {
  if (safetyFunctionId == null || safetyFunctionId.trim() === '') {
    return null
  }

  const linkedSafetyFunction = safetyFunctions.find(
    (safetyFunction) => safetyFunction.id === safetyFunctionId
  )

  if (linkedSafetyFunction == null) {
    return {
      name: safetyFunctionId,
      eplanReferenceId: null,
    }
  }

  const eplanReferenceId = linkedSafetyFunction.eplanReferenceId
  const hasEplanReferenceId =
    eplanReferenceId != null && eplanReferenceId.trim() !== ''

  return {
    name: linkedSafetyFunction.name,
    eplanReferenceId: hasEplanReferenceId ? eplanReferenceId : null,
  }
}

export function toRiskReductionMeasureFormData(
  riskReductionMeasure: RiskReductionMeasure
): RiskReductionMeasureFormData {
  return {
    name: riskReductionMeasure.name,
    type: riskReductionMeasure.type,
    description: riskReductionMeasure.description ?? '',
    dependsOnControlSystem:
      riskReductionMeasure.dependsOnControlSystem ?? false,
    safetyFunctionId: riskReductionMeasure.safetyFunctionId ?? null,
    ehsrClauseIds: riskReductionMeasure.ehsrClauseIds ?? [],
  }
}

export function toRiskReductionMeasureInput(
  data: RiskReductionMeasureFormData
): RiskReductionMeasureInput {
  const isEligible = isEligibleForSafetyFunctionAssignment(data)

  return {
    name: data.name.trim(),
    type: data.type,
    description: data.description.trim(),
    dependsOnControlSystem:
      data.type === 'SCP' ? data.dependsOnControlSystem : null,
    safetyFunctionId: isEligible ? data.safetyFunctionId : null,
    ehsrClauseIds:
      data.ehsrClauseIds.length > 0 ? data.ehsrClauseIds : null,
  }
}

export function toSafetyFunctionAssignmentInput(
  riskReductionMeasure: RiskReductionMeasure,
  safetyFunctionId: string | null
): RiskReductionMeasureInput {
  const ehsrClauseIds = riskReductionMeasure.ehsrClauseIds ?? []

  return {
    name: riskReductionMeasure.name,
    type: riskReductionMeasure.type,
    description: riskReductionMeasure.description ?? '',
    dependsOnControlSystem: riskReductionMeasure.dependsOnControlSystem,
    safetyFunctionId,
    ehsrClauseIds: ehsrClauseIds.length > 0 ? ehsrClauseIds : null,
  }
}

export function toEhsrClauseIdsUpdateInput(
  riskReductionMeasure: RiskReductionMeasure,
  ehsrClauseIds: string[]
): RiskReductionMeasureInput {
  return {
    name: riskReductionMeasure.name,
    type: riskReductionMeasure.type,
    description: riskReductionMeasure.description ?? '',
    dependsOnControlSystem: riskReductionMeasure.dependsOnControlSystem,
    safetyFunctionId: riskReductionMeasure.safetyFunctionId,
    ehsrClauseIds: ehsrClauseIds.length > 0 ? ehsrClauseIds : null,
  }
}
